import {
  Box,
  FormControl,
  FormLabel,
  Grid2 as Grid,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  MenuItem,
  Button,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { FormProvider } from "react-hook-form";
import { SaveButton } from "@/organism";
import useCreateForm from "./useCreateForm";
import { Yn } from "@/constants/code";
import { ChangeEvent, useRef, useState } from "react";

export type CreateFormProps = {
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
};

export const CreateForm = (props: CreateFormProps) => {
  const { onSubmit, isLoading } = props;
  const { methods, handleSubmit } = useCreateForm(onSubmit);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>("");

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      methods.setValue("file", files[0]);
      setSelectedFileName(files[0].name);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const defaultProps = {
    size: "small" as const,
    error: false,
    helperText: "",
    placeholder: "입력해주세요",
  };

  return (
    <FormProvider {...methods}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: "blur(3px)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" flexDirection="column" gap={4}>
        <Box>
          <Grid container spacing={2}>
            {/* 1. 기본 정보 */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                기본 정보
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormControl fullWidth>
                <FormLabel required>템플릿명</FormLabel>
                <TextField
                  {...methods.register("templateNm")}
                  {...defaultProps}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <FormControl fullWidth>
                <FormLabel required>구독 상품</FormLabel>
                <TextField
                  select
                  {...methods.register("productId")}
                  {...defaultProps}
                >
                  <MenuItem value="1">Starter</MenuItem>
                  <MenuItem value="2">Standard</MenuItem>
                  <MenuItem value="3">Premium</MenuItem>
                </TextField>
              </FormControl>
            </Grid>

            {/* 2. 템플릿 이미지 */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                템플릿 썸네일 이미지
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 12 }}>
              <FormControl fullWidth>
                <FormLabel required>썸네일 이미지</FormLabel>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Button variant="outlined" onClick={handleFileButtonClick}>
                      파일 선택
                    </Button>
                    <Typography variant="body2">
                      {selectedFileName || "선택된 파일 없음"}
                    </Typography>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      ref={fileInputRef}
                      onChange={handleFileChange}
                    />
                  </Box>

                  {/* 이미지 미리보기 */}
                  {selectedFileName && (
                    <Box
                      sx={{
                        mt: 2,
                        maxWidth: 300,
                        borderRadius: 1,
                        overflow: "hidden",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <img
                        src={
                          methods.watch("file")
                            ? URL.createObjectURL(methods.watch("file") as File)
                            : ""
                        }
                        alt="썸네일 미리보기"
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </FormControl>
            </Grid>

            {/* 3. 템플릿 정보 */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                템플릿 정보
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 12 }}>
              <FormControl fullWidth>
                <FormLabel required>템플릿 URL</FormLabel>
                <TextField
                  {...methods.register("templateUrl")}
                  {...defaultProps}
                  placeholder="https://example.com/template"
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, lg: 12 }}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>템플릿 상세 설명</FormLabel>
                <TextField
                  multiline
                  rows={4}
                  {...methods.register("templateDtl")}
                  {...defaultProps}
                />
              </FormControl>
            </Grid>

            {/* 4. 노출 설정 */}
            <Grid size={{ xs: 12, lg: 12 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                노출 설정
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={methods.watch("dispYn") === Yn.Y}
                    onChange={(e) =>
                      methods.setValue("dispYn", e.target.checked ? Yn.Y : Yn.N)
                    }
                  />
                }
                label="템플릿 노출"
              />
            </Grid>
          </Grid>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="center" mt={4}>
          <SaveButton onClick={handleSubmit}>저장</SaveButton>
        </Box>
      </Box>
    </FormProvider>
  );
};
